import { action } from 'mobx';
import { ToastType } from '@honeybook/hbui/dist/components/HBToast/HBToast.types';
import type { RootStore } from '~/stores/root.store';
import { raceFunction } from '~/utils/data.utils';
import { TGalleryDashboardSortBy } from '~/components/GalleryDashboard/gallery-dashboard.types';
import { TRepoKey } from '~/components/FlowGallery/types/flow-gallery.types';

export class GalleryDashboardController {
    rootStore: RootStore = null;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
    }

    @action.bound
    async loadGalleryDashboard() {
        const { toastStore } = this.rootStore;

        try {
            await this.fetchTemplates();
        } catch (error) {
            toastStore.add({
                type: ToastType.Error,
                text: 'Could not load the templates'
            });
        }
    }

    @action.bound
    fetchTemplates = raceFunction(() => {
        const {
            templatesService,
            galleryDashboardUiStore: {
                setLoadingStatuses,
                loadTemplates,
                setAllRepoTags,
                currentRepository
            }
        } = this.rootStore;

        setLoadingStatuses('loadTemplates', true);

        templatesService
            .fetchTemplatesRepository(currentRepository)
            .then(repo => {
                setAllRepoTags(repo.tags, repo.system_attributes);
            });

        return {
            try: () =>
                templatesService.fetchTemplates({
                    skipMicroService: true,
                    repository: currentRepository
                }),
            set: loadTemplates,
            finally: () => setLoadingStatuses('loadTemplates', false)
        };
    });

    @action.bound
    async publishTemplate(repos: TRepoKey[]) {
        const {
            toastStore,
            templatesService,
            galleryDashboardUiStore: {
                closeModalAndResetState,
                setLoadingStatuses,
                selectedTemplate
            }
        } = this.rootStore;

        setLoadingStatuses('publishTemplate', true);

        try {
            await templatesService.publishGalleryTemplate(
                selectedTemplate,
                repos
            );

            await this.deleteTemplate(true);

            toastStore.add({
                type: ToastType.Success,
                text: 'Template published successfully!'
            });
        } catch (error) {
            toastStore.add({
                type: ToastType.Error,
                text: 'Could not publish the template'
            });
        } finally {
            setLoadingStatuses('publishTemplate', false);
            closeModalAndResetState();
        }
    }

    @action.bound
    async prepublishTemplate() {
        const {
            toastStore,
            templatesService,
            galleryDashboardUiStore: {
                selectedTemplate,
                addTemplate,
                setLoadingStatuses,
                closeModalAndResetState
            }
        } = this.rootStore;

        setLoadingStatuses('publishTemplate', true);

        try {
            const newTemplate = await templatesService.publishGalleryTemplate(
                selectedTemplate,
                ['prepublish_templates']
            );

            addTemplate(newTemplate);

            toastStore.add({
                type: ToastType.Success,
                text: 'Template created successfully!'
            });
        } catch (error) {
            toastStore.add({
                type: ToastType.Error,
                text: `Could not create the template ${selectedTemplate?.title}`
            });
        }

        setLoadingStatuses('publishTemplate', false);

        closeModalAndResetState();
    }

    @action.bound
    async saveTemplate() {
        const {
            toastStore,
            templatesService,
            galleryDashboardUiStore: {
                selectedTemplate,
                mergeResponse,
                setLoadingStatuses,
                closeModalAndResetState,
                currentRepository
            }
        } = this.rootStore;

        setLoadingStatuses('saveTemplate', true);

        try {
            const response = await templatesService.saveGalleryTemplate(
                selectedTemplate,
                currentRepository
            );

            mergeResponse(response);

            toastStore.add({
                type: ToastType.Success,
                text: 'Template saved successfully!'
            });
        } catch (error) {
            toastStore.add({
                type: ToastType.Error,
                text: 'Could not save the template'
            });
        } finally {
            setLoadingStatuses('saveTemplate', false);
            closeModalAndResetState();
        }
    }

    @action.bound
    async deleteTemplate(hideToast = false) {
        const {
            toastStore,
            templatesService,
            galleryDashboardUiStore: {
                selectedTemplate,
                currentRepository,
                deleteCurrentTemplate,
                setLoadingStatuses,
                closeModalAndResetState
            }
        } = this.rootStore;

        setLoadingStatuses('deleteTemplate', true);

        try {
            await templatesService.deleteGalleryTemplate(
                selectedTemplate.template_id,
                currentRepository
            );

            deleteCurrentTemplate();

            if (!hideToast) {
                toastStore.add({
                    type: ToastType.Success,
                    text: 'Template Deleted successfully!'
                });
            }
        } catch (error) {
            toastStore.add({
                type: ToastType.Error,
                text: 'Could not delete the template'
            });
        } finally {
            setLoadingStatuses('deleteTemplate', false);
            closeModalAndResetState();
        }
    }

    @action.bound
    setSortBy(sortBy: TGalleryDashboardSortBy) {
        const {
            galleryDashboardUiStore: { setSortBy }
        } = this.rootStore;

        setSortBy(sortBy);
    }

    @action.bound
    async setCurrentRepository(newRepository: TRepoKey) {
        const {
            galleryDashboardUiStore: { currentRepository, setCurrentRepository }
        } = this.rootStore;

        if (currentRepository !== newRepository) {
            setCurrentRepository(newRepository);
            await this.fetchTemplates();
        }
    }

    @action.bound
    async getUserTemplates() {
        const {
            galleryDashboardUiStore: {
                setUserTemplates,
                searchedUserMail,
                setIsInvalidUser
            },
            templatesService
        } = this.rootStore;
        try {
            const templates = await templatesService.getUserTemplates(
                searchedUserMail
            );
            setUserTemplates(templates);
            setIsInvalidUser(false);
        } catch (error) {
            setUserTemplates([]);
            setIsInvalidUser(true);
        }
    }

    @action.bound
    async publishImportedTemplates() {
        const {
            galleryDashboardUiStore: {
                importedSelectedTemplates,
                editSelectedTemplate,
                setSelectedImportedTemplates
            }
        } = this.rootStore;

        importedSelectedTemplates?.forEach(template => {
            editSelectedTemplate({
                template_id: template.id,
                title: template.name
            });
            this.prepublishTemplate();
        });
        setSelectedImportedTemplates([]);
        editSelectedTemplate(undefined);
    }

    @action.bound
    async createNewTag(newTag: string) {
        const {
            toastStore,
            templatesService,
            galleryDashboardUiStore: {
                currentRepository,
                repoTags,
                selectedTemplate,
                editSelectedTemplate,
                setAllRepoTags
            }
        } = this.rootStore;

        try {
            const { tags, system_attributes } =
                await templatesService.updateTags(currentRepository, [
                    ...repoTags,
                    newTag
                ]);

            if (tags) {
                setAllRepoTags(tags, system_attributes);
                editSelectedTemplate({
                    tags: !selectedTemplate?.tags
                        ? [newTag]
                        : [...selectedTemplate.tags, newTag]
                });
            }

            toastStore.add({
                type: ToastType.Success,
                text: 'Tag created successfully!'
            });
        } catch (error) {
            toastStore.add({
                type: ToastType.Error,
                text: 'Could not create the tag.'
            });
        }
    }
}
